import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "./api";

/**
 * 🔴 `data-tb-inflight` IS A CONTRACT, NOT A DEBUG AID. A heading renders before its data,
 * so grepping rendered text once reported PASS against a hung deployment. This attribute is
 * what a browser check waits on to know the page actually settled — it is
 * protocol-independent, which is why it survived the move off Windmill runnables to HTTP.
 *
 * It counts REQUESTS IN FLIGHT ACROSS THE WHOLE APP, not per component: a page with one
 * settled panel and one still loading is not settled.
 */
let inflight = 0;
function bump(delta: number) {
  inflight = Math.max(0, inflight + delta);
  document.documentElement.dataset.tbInflight = String(inflight);
}

export interface Async<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  /** ⚠️ WHEN THIS DATA WAS FETCHED. A panel that does not say how old it is reads as live;
   *  `<Freshness>` renders it beside the Refresh button. */
  fetchedAt: number | null;
  reload: () => void;
}

export interface AsyncOpts {
  /** Refetch every N milliseconds. Used by the one query the whole shell shares, so the
   *  sidebar's counts and the Overview do not go stale while a teacher watches a run.
   *
   *  ⚠️ IT BUMPS THE SAME NONCE `reload` DOES, deliberately: the refresh then travels through
   *  the one fetch effect below, so the sequence guard and the `data-tb-inflight` counter
   *  apply to it unchanged. A second fetch path would have needed its own copy of both.
   *  ⚠️ PAUSED WHILE THE TAB IS HIDDEN. A background tab polling every minute for a day is
   *  1,440 requests nobody read — the same shape as the healthcheck that buried the log. */
  every?: number;
}

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[],
                            opts: AsyncOpts = {}): Async<T> {
  const [data, setData] = useState<T | null>(null);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  // ⚠️ A STALE RESPONSE MUST NOT OVERWRITE A FRESH ONE. Switching cohorts fires a second
  // request while the first is still open, and without this the slower — older — answer
  // wins and the page shows another course's rows under this course's heading.
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    setLoading(true);
    bump(1);
    let done = false;
    fn()
      .then((d) => {
        if (mine === seq.current) { setData(d); setError(null); setFetchedAt(Date.now()); }
      })
      .catch((e) => { if (mine === seq.current) { setError(e as ApiError); setData(null); } })
      .finally(() => {
        if (!done) { done = true; bump(-1); }
        if (mine === seq.current) setLoading(false);
      });
    return () => { if (!done) { done = true; bump(-1); } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const every = opts.every ?? 0;
  useEffect(() => {
    if (!every) return;
    const id = window.setInterval(() => {
      if (!document.hidden) setNonce((n) => n + 1);
    }, every);
    return () => window.clearInterval(id);
  }, [every]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, error, loading, fetchedAt, reload };
}

/** Wrap a one-off mutation so it counts toward `data-tb-inflight` too. */
export async function tracked<T>(p: Promise<T>): Promise<T> {
  bump(1);
  try { return await p; } finally { bump(-1); }
}
